import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { nanoid } from 'nanoid'
import { uniquePreserveOrder } from '@/util/array-utils'
import type { FileSelectionPrecondition, FolderPathPrecondition, FolderSelectionPrecondition } from '@/user_files/schemas/preconditions'

export enum UserFilesBatchedOperationKind {
  MoveSelection = 'move_selection',
  DeleteSelection = 'delete_selection',
}

export enum UserFilesBatchedOperationStatus {
  Queued = 'queued',
  Running = 'running',
  Succeeded = 'succeeded',
  Failed = 'failed',
}

export type UserFilesBatchedOperation = {
  id: string
  kind: UserFilesBatchedOperationKind
  status: UserFilesBatchedOperationStatus
  createdAt: number
  startedAt?: number
  finishedAt?: number

  sourceFolderId: string
  destinationFolderId?: string

  fileIds: string[]
  folderIds: string[]
  filePreconditions?: FileSelectionPrecondition[]
  folderPreconditions?: FolderSelectionPrecondition[]
  destinationFolderPrecondition?: FolderPathPrecondition
  totalItems: number
  processedItems: number

  errorMessage?: string
}

export type EnqueueDeleteSelectionPayload = {
  sourceFolderId: string
  fileIds?: string[]
  folderIds?: string[]
  filePreconditions?: FileSelectionPrecondition[]
  folderPreconditions?: FolderSelectionPrecondition[]
}

export type EnqueueMoveSelectionPayload = {
  sourceFolderId: string
  destinationFolderId: string
  fileIds?: string[]
  folderIds?: string[]
  filePreconditions?: FileSelectionPrecondition[]
  folderPreconditions?: FolderSelectionPrecondition[]
  destinationFolderPrecondition?: FolderPathPrecondition
}

export type UserFilesBatchingState = {
  activeOperation: UserFilesBatchedOperation | null
}

const initialState: UserFilesBatchingState = {
  activeOperation: null,
}

export const userFilesBatchingSlice = createSlice({
  name: 'userFilesBatchingState',
  initialState,
  reducers: {
    enqueueDeleteSelection: (state, action: PayloadAction<EnqueueDeleteSelectionPayload>) => {
      const isActive =
        state.activeOperation?.status === UserFilesBatchedOperationStatus.Queued || state.activeOperation?.status === UserFilesBatchedOperationStatus.Running
      if (isActive) return

      const fileIds = uniquePreserveOrder(action.payload.fileIds ?? [])
      const folderIds = uniquePreserveOrder(action.payload.folderIds ?? [])
      if (fileIds.length === 0 && folderIds.length === 0) return

      state.activeOperation = {
        id: nanoid(),
        kind: UserFilesBatchedOperationKind.DeleteSelection,
        status: UserFilesBatchedOperationStatus.Queued,
        createdAt: Date.now(),
        sourceFolderId: action.payload.sourceFolderId,
        fileIds,
        folderIds,
        filePreconditions: action.payload.filePreconditions,
        folderPreconditions: action.payload.folderPreconditions,
        totalItems: fileIds.length + folderIds.length,
        processedItems: 0,
      }
    },

    enqueueMoveSelection: (state, action: PayloadAction<EnqueueMoveSelectionPayload>) => {
      const isActive =
        state.activeOperation?.status === UserFilesBatchedOperationStatus.Queued || state.activeOperation?.status === UserFilesBatchedOperationStatus.Running
      if (isActive) return

      const fileIds = uniquePreserveOrder(action.payload.fileIds ?? [])
      const folderIds = uniquePreserveOrder(action.payload.folderIds ?? [])
      if (fileIds.length === 0 && folderIds.length === 0) return

      state.activeOperation = {
        id: nanoid(),
        kind: UserFilesBatchedOperationKind.MoveSelection,
        status: UserFilesBatchedOperationStatus.Queued,
        createdAt: Date.now(),
        sourceFolderId: action.payload.sourceFolderId,
        destinationFolderId: action.payload.destinationFolderId,
        fileIds,
        folderIds,
        filePreconditions: action.payload.filePreconditions,
        folderPreconditions: action.payload.folderPreconditions,
        destinationFolderPrecondition: action.payload.destinationFolderPrecondition,
        totalItems: fileIds.length + folderIds.length,
        processedItems: 0,
      }
    },

    operationStarted: (state, action: PayloadAction<{ operationId: string }>) => {
      const op = state.activeOperation
      if (!op || op.id !== action.payload.operationId) return
      op.status = UserFilesBatchedOperationStatus.Running
      op.startedAt = Date.now()
      op.processedItems = 0
      delete op.errorMessage
    },

    operationProgressUpdated: (state, action: PayloadAction<{ operationId: string; processedItems: number }>) => {
      const op = state.activeOperation
      if (!op || op.id !== action.payload.operationId) return
      op.processedItems = action.payload.processedItems
    },

    operationSucceeded: (state, action: PayloadAction<{ operationId: string }>) => {
      const op = state.activeOperation
      if (!op || op.id !== action.payload.operationId) return
      op.status = UserFilesBatchedOperationStatus.Succeeded
      op.finishedAt = Date.now()
      op.processedItems = op.totalItems
    },

    operationFailed: (state, action: PayloadAction<{ operationId: string; errorMessage: string }>) => {
      const op = state.activeOperation
      if (!op || op.id !== action.payload.operationId) return
      op.status = UserFilesBatchedOperationStatus.Failed
      op.finishedAt = Date.now()
      op.errorMessage = action.payload.errorMessage
    },

    clearActiveOperation: (state) => {
      state.activeOperation = null
    },
  },
})

export const UserFilesBatchingActions = userFilesBatchingSlice.actions
export default userFilesBatchingSlice.reducer
